import { DrugSearchResMock } from '../../../../../types/drug.type'
import { MedFrequencyResMock } from '../../../../../types/med_frequency.type'
import {
  DEFAULT_SUPPLY_SOURCE,
  FrequencyData,
  MedicationDataMock,
  MedicationIncluded,
  SupplySource
} from '../../../../../types/medication.type'
import { medicationDurationDays } from '../../../../../utils/formatter'

export const SUPPLY_SOURCES: SupplySource[] = ['in_house', 'outside']

export const isSupplySource = (value: unknown): value is SupplySource => SUPPLY_SOURCES.includes(value as SupplySource)

/** Frequency as the Add Medication form holds it (display name + custom-frequency data). */
export type PrefillFrequency = {
  selectedFrequency: string
  freqData: FrequencyData | null
  isCustomFrequencySelected: boolean
}

/** The form's dose unit before anyone picks one. */
export const DEFAULT_DOSE_UNIT = 'mg'
/** The form's order length before anyone sets one (end_at = start_at + this). */
export const DEFAULT_DURATION_DAYS = 5

/**
 * Values to put into the order form. A missing key means "leave alone". An empty string
 * (or `null` frequency, `false` once, the default dose unit, `resetEndAt`) means "back to the
 * default" — used to take back our own earlier prefill.
 */
export type MedicationPrefill = {
  form?: string
  strength?: string
  route?: string
  dose_quantity?: string
  dose_unit?: string
  frequency?: PrefillFrequency | null
  once?: boolean
  /** Who supplies the medicine (the default `in_house` means "back to the default"). */
  supply_source?: SupplySource
  /** Order length in whole days; the form sets end_at = start_at + this. */
  durationDays?: number
  /** Put end_at back to the form default (start_at + 5 days). */
  resetEndAt?: boolean
}

/** What the form currently holds, as far as prefill cares. */
export type PrefillTarget = {
  form: string
  strength: string
  route: string
  dose_quantity: string
  dose_unit: string
  start_at: string
  end_at: string
  /** Display value of the frequency field ('' when none). */
  frequency: string
  once: boolean
  /** The end date was changed by hand (directly, via the duration, or moved along with the start). */
  endAtTouched: boolean
  /** Supply choice; missing counts as the default (`in_house`). */
  supply_source?: SupplySource
}

/** Whole days between the form's start and end ('' / invalid dates → null). */
const currentDurationDays = (current: PrefillTarget) => medicationDurationDays(current.start_at, current.end_at)

const formatPeriods = (periods: string[]) => {
  if (periods.length > 2) {
    return `${periods.slice(0, -1).join(', ')}, and ${periods[periods.length - 1]}`
  }
  return periods.join(' and ')
}

export const renderFrequencyOptions = (frequencyData?: FrequencyData | null): string => {
  if (!frequencyData) return ''

  const { interval = '', periods = [], type = '' } = frequencyData

  if (type === 'hourly') return interval
  if (type === 'daily') {
    return periods.length > 0 ? `Every ${formatPeriods(periods)}` : 'Daily'
  }
  return ''
}

/** Usual route for a dosage form. Only unambiguous forms are mapped. */
export const ROUTE_BY_FORM: Record<string, string> = {
  capsule: 'Oral',
  cream: 'Topical',
  gel: 'Topical',
  granule: 'Oral',
  infusion: 'Intravenous',
  inhaler: 'Inhalation',
  lotion: 'Topical',
  ointment: 'Topical',
  patch: 'Transdermal',
  suppository: 'Rectal',
  suspension: 'Oral',
  syrup: 'Oral',
  tablet: 'Oral'
}

const norm = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase()

const isBlank = (value: unknown) => norm(value) === ''

/** Route for a form, spelled as in the configured routes list when it is there. */
export const routeForForm = (form: string | undefined, routes: string[] = []): string => {
  const route = ROUTE_BY_FORM[norm(form)]
  if (!route) return ''
  return routes.find(option => norm(option) === norm(route)) ?? route
}

/** Defaults from the drug catalog: only when exactly one hit has the chosen name. */
export const catalogDefaults = (
  drugName: string,
  hits: DrugSearchResMock[] | undefined,
  routes: string[] = []
): MedicationPrefill | null => {
  const matches = (hits ?? []).filter(hit => norm(hit?.name) === norm(drugName))
  if (!norm(drugName) || matches.length !== 1) return null

  const [drug] = matches
  const prefill: MedicationPrefill = {}
  if (!isBlank(drug.form)) {
    prefill.form = drug.form
    const route = routeForForm(drug.form, routes)
    if (route) prefill.route = route
  }
  const strengths = Array.from(
    new Set(
      (drug.available_strengths ?? [])
        .map(combination => (combination ?? []).map(s => s?.strength).join('/'))
        .filter(label => label !== '')
    )
  )
  if (strengths.length === 1) prefill.strength = strengths[0]
  return prefill
}

const frequencyOf = (
  medication: MedicationDataMock,
  included: MedicationIncluded[] = [],
  medFrequencies: MedFrequencyResMock[] = []
): PrefillFrequency | undefined => {
  const custom = medication.attributes?.custom_frequency as FrequencyData | undefined | null
  if (custom?.type) {
    if (!renderFrequencyOptions(custom)) return undefined
    return {
      freqData: custom,
      isCustomFrequencySelected: true,
      selectedFrequency: renderFrequencyOptions(custom)
    }
  }
  const id = medication.relationships?.medication_frequency?.data?.id
  if (!id) return undefined
  const fromIncluded = included.find(item => item?.type === 'medication_frequency' && item?.id === id) as any
  const name = fromIncluded?.attributes?.name || medFrequencies.find(freq => freq?.id === id)?.attributes?.name
  return name ? { freqData: null, isCustomFrequencySelected: false, selectedFrequency: name } : undefined
}

/** Defaults from the prescriber's previous order of the same drug. */
export const lastOrderDefaults = (
  medication: MedicationDataMock,
  included: MedicationIncluded[] = [],
  medFrequencies: MedFrequencyResMock[] = []
): MedicationPrefill => {
  const attributes = (medication.attributes ?? {}) as Partial<MedicationDataMock['attributes']>
  const prefill: MedicationPrefill = {}

  if (!isBlank(attributes.form)) prefill.form = attributes.form
  if (!isBlank(attributes.strength)) prefill.strength = attributes.strength
  if (!isBlank(attributes.route)) prefill.route = attributes.route

  const [dosageQuantity, dosageUnit] = String(attributes.dosage ?? '')
    .trim()
    .split(/\s+/)
  const quantity = isBlank(attributes.dose_quantity) ? dosageQuantity : String(attributes.dose_quantity)
  const unit = isBlank(attributes.dose_unit) ? dosageUnit : attributes.dose_unit
  if (!isBlank(quantity)) {
    prefill.dose_quantity = String(quantity).trim()
    if (!isBlank(unit)) prefill.dose_unit = String(unit).trim()
  }

  const frequency = frequencyOf(medication, included, medFrequencies)
  if (frequency) prefill.frequency = frequency
  if (attributes.once === true) prefill.once = true
  // Only a non-default choice is worth repeating; the default is what the form starts with anyway.
  if (isSupplySource(attributes.supply_source) && attributes.supply_source !== DEFAULT_SUPPLY_SOURCE) {
    prefill.supply_source = attributes.supply_source
  }

  const days = medicationDurationDays(attributes.start_at, attributes.end_at)
  if (days != null && days > 0) prefill.durationDays = days

  return prefill
}

const currentSupply = (current: PrefillTarget): SupplySource => current.supply_source ?? DEFAULT_SUPPLY_SOURCE

/** Keep only the parts of `prefill` whose field is still empty in the form. */
export const onlyEmptyFields = (prefill: MedicationPrefill, current: PrefillTarget): MedicationPrefill => {
  const result: MedicationPrefill = {}
  if (prefill.form && isBlank(current.form)) result.form = prefill.form
  if (prefill.strength && isBlank(current.strength)) result.strength = prefill.strength
  if (prefill.route && isBlank(current.route)) result.route = prefill.route
  if (prefill.dose_quantity && isBlank(current.dose_quantity)) {
    result.dose_quantity = prefill.dose_quantity
    if (prefill.dose_unit) result.dose_unit = prefill.dose_unit
  }
  if (prefill.frequency && isBlank(current.frequency)) result.frequency = prefill.frequency
  if (prefill.once && !current.once) result.once = true
  // The supply choice is "empty" while it still shows the default.
  if (
    prefill.supply_source &&
    prefill.supply_source !== DEFAULT_SUPPLY_SOURCE &&
    currentSupply(current) === DEFAULT_SUPPLY_SOURCE
  ) {
    result.supply_source = prefill.supply_source
  }
  // Moving only the start shifts the end too, so a still-default length counts as not chosen.
  const durationChosen = current.endAtTouched && currentDurationDays(current) !== DEFAULT_DURATION_DAYS
  if (prefill.durationDays && !durationChosen) result.durationDays = prefill.durationDays
  return result
}

/** The field values a prefill put into the form, to recognise them later. */
export type AutoFilled = Partial<
  Pick<
    PrefillTarget,
    'form' | 'strength' | 'route' | 'dose_quantity' | 'dose_unit' | 'frequency' | 'once' | 'supply_source'
  >
> & { durationDays?: number }

export const recordAutoFilled = (applied: MedicationPrefill): AutoFilled => {
  const record: AutoFilled = {}
  if (applied.form) record.form = applied.form
  if (applied.strength) record.strength = applied.strength
  if (applied.route) record.route = applied.route
  if (applied.dose_quantity) record.dose_quantity = applied.dose_quantity
  if (applied.dose_unit) record.dose_unit = applied.dose_unit
  if (applied.durationDays) record.durationDays = applied.durationDays
  if (applied.frequency) record.frequency = applied.frequency.selectedFrequency
  if (applied.once) record.once = true
  if (applied.supply_source && applied.supply_source !== DEFAULT_SUPPLY_SOURCE) {
    record.supply_source = applied.supply_source
  }
  return record
}

/**
 * When the drug changes, clear what our previous prefill put in and the prescriber left as is,
 * so values from the old drug do not stick to the new one. Edited fields are kept.
 */
export const takeBackAutoFill = (autoFilled: AutoFilled, current: PrefillTarget): MedicationPrefill => {
  const clear: MedicationPrefill = {}
  if (autoFilled.form && current.form === autoFilled.form) clear.form = ''
  if (autoFilled.strength && current.strength === autoFilled.strength) clear.strength = ''
  if (autoFilled.route && current.route === autoFilled.route) clear.route = ''
  if (autoFilled.dose_quantity && current.dose_quantity === autoFilled.dose_quantity) clear.dose_quantity = ''
  // The unit belongs to the dose: keep it with a dose the prescriber typed (2 g must not become 2 mg).
  const doseGone = clear.dose_quantity === '' || isBlank(current.dose_quantity)
  if (
    autoFilled.dose_unit &&
    doseGone &&
    current.dose_unit === autoFilled.dose_unit &&
    current.dose_unit !== DEFAULT_DOSE_UNIT
  ) {
    clear.dose_unit = DEFAULT_DOSE_UNIT
  }
  // Still our length (even if both dates moved with the start) → back to the default length.
  if (autoFilled.durationDays && currentDurationDays(current) === autoFilled.durationDays) clear.resetEndAt = true
  if (autoFilled.frequency && current.frequency === autoFilled.frequency) clear.frequency = null
  if (autoFilled.once && current.once) clear.once = false
  if (autoFilled.supply_source && currentSupply(current) === autoFilled.supply_source) {
    clear.supply_source = DEFAULT_SUPPLY_SOURCE
  }
  return clear
}
